import React from "react";
import { LinkBox, LinkOverlay, useMediaQuery } from "@chakra-ui/react";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Image,
  Link,
  Text,
} from "@chakra-ui/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y } from "swiper";
import "swiper/css";
import "swiper/css/a11y";
import "./cards.scss";
import { CardsModuleProps } from "../../types/homePageTypes";


//  Grid Layout: 1 | 2 | 2

const Cards = ({ title, articleItems }: CardsModuleProps) => {
  const [isLargerThan1200] = useMediaQuery("(min-width: 1200px)");
  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const [isLargerThan567] = useMediaQuery("(min-width: 567px)");
  const [isSmallerThan567] = useMediaQuery("(max-width: 566px)");

  let slidesPerView;
  if (isSmallerThan567) {
    slidesPerView = 1.25;
  } else if (isLargerThan567 && !isLargerThan768) {
    slidesPerView = 1.28;
  } else if (isLargerThan768 && !isLargerThan1200) {
    slidesPerView = 1.33;
  } else {
    slidesPerView = 2.33;
  }

  // case: not enough cards
  if (articleItems.length < 5) {
    console.log(`Section "${title}" is not importing enough cards to build the module. Please check Contentful for proper selection of grid layout or that the right number of cards has been added to the object.`);
  }

  // case: too many cards, cutoff at needed amount
  const MAX_CARDS = 5;
  let finalCards = articleItems;
  if (articleItems.length > MAX_CARDS) {
    finalCards = articleItems.slice(0, MAX_CARDS);
  }

  return (
    <Box
      className="cards-1-2-2-section"
      marginBottom={{ base: "50px !important", sm: "80px !important" }} 
      marginTop="0 !important"
    >
      {/* section title */}
      <Heading
        as="h2"
        size={{ base: "section-header-mobile", sm: "section-header-tablet", lg: "section-header-desktop" }} 
        marginBottom={{ base: "16px !important", sm: "33px !important" }}
        marginLeft={{ base: "24px !important", sm: "35px !important" }}
      >
        { title }
      </Heading>

      {/* mobile view */}
      {!isLargerThan1200 &&
        <>
          <Grid
            templateColumns="1, 1fr"
            gap="10px"
            marginRight="10px"
            marginLeft="10px"
          >
            {/* card 1 */}
            <GridItem
              rowSpan={ 1 } 
              colSpan={ 1 } 
              marginBottom="10px"
            >
              <LinkBox>
                <LinkOverlay href={ finalCards[0].slug }>
                  <Box
                    w="100%"
                    height={{ base: "178px", sm: "365px" }}
                    backgroundImage={ finalCards[0].imageUrl }
                    aria-label={ finalCards[0].imageAlt }
                    backgroundRepeat="no-repeat"
                    backgroundSize="cover"
                    backgroundPosition="center"
                  >
                    <Flex
                      w="100%" 
                      h="100%"
                      p={{ base: "12px 33px", sm: "30px 70px" }}
                      direction="column"
                      justifyContent="flex-end !important"
                      alignItems="flex-start !important"
                      // background="linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.008) 18.4%, rgba(0, 0, 0, 0.027) 33.8%, rgba(0, 0, 0, 0.06) 46.5%, rgba(0, 0, 0, 0.1) 56.8%, rgba(0, 0, 0, 0.15) 65%, rgba(0, 0, 0, 0.2) 71.5%, rgba(0, 0, 0, 0.255) 76.5%, rgba(0, 0, 0, 0.31) 80.3%, rgba(0, 0, 0, 0.365) 83.2%, rgba(0, 0, 0, 0.416) 85.6%, rgba(0, 0, 0, 0.463) 87.8%, rgba(0, 0, 0, 0.5) 90%, rgba(0, 0, 0, 0.533) 92.6%, rgba(0, 0, 0, 0.553) 95.8%, rgba(0, 0, 0, 0.56) 100%)"
                    >

                      {/* label */}
                      {(finalCards[0] && finalCards[0].label) &&
                        <Box
                          display="flex"
                          flexDirection="row"
                          gap={{ base: "10px", sm: "15px" }}
                          marginBottom={{ base: "8px", sm:"16px", lg: "24px" }}
                        >
                          <Text
                            size={{ base: "schedule-item-tag-mobile", sm: "schedule-item-tag" }}
                            textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                            backgroundColor="rgba(0,0,0,0.01)"
                          >
                            { finalCards[0].label }
                          </Text>
                        </Box>
                      }

                      {/* heading */}
                      {(finalCards[0] && finalCards[0].title) &&
                        <Box
                          marginBottom={{ base: "10px", sm: "16px" }}
                        >
                          <Heading
                            as="h3"
                            size={{ base: "cards-header-mobile", sm: "cards-header" }}
                            textAlign="left"
                            textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                            backgroundColor="rgba(0,0,0,0.01)"
                          >
                            { finalCards[0].title }
                          </Heading>
                        </Box>
                      }

                      {/* button */}
                      {(finalCards[0] && finalCards[0].buttonText) &&
                        <Button
                          size={{ base: "poster-slider-mobile", sm: "poster-slider" }}
                          backgroundColor="#E31D40 !important"
                        >
                          { finalCards[0].buttonText } 
                        </Button>
                      
                      }
                    </Flex>
                  </Box>
                </LinkOverlay>
              </LinkBox>
            </GridItem>
          </Grid>

          {/* card 2 - 5 slider */}
          <Swiper
            className="cards-1-2-2"
            modules={[ A11y ]}
            slidesPerView={ slidesPerView }
            spaceBetween={ 10 }
          >
            {finalCards.map(( card, index ) => {
              // only cards 2 - 4
              if (index === 0) {
                return null
              }

              return (
                <SwiperSlide key={ index }>
                  <LinkBox>
                    <LinkOverlay href={ card.slug } aria-label={ card.buttonText }>
                      <Image
                        h={{ base: "142px", sm: "283px" }}
                        w="100%"
                        src={ card.imageUrl }
                        alt={ card.imageAlt }
                        objectFit="cover"
                      />
                    </LinkOverlay>
                  </LinkBox>
                  <Flex
                    w="100%"
                    p={{ base: "30px 20px 40px 20px", sm: "40px 30px 50px 30px" }}
                    direction="column"
                    justifyContent="flex-start !important"
                  >

                    {/* label */}
                    {card.label &&
                      <Box
                        display="flex"
                        flexDirection="row"
                        gap={{ base: "10px", sm: "15px" }}
                        marginBottom={{ base: "16px !important", sm: "21px !important" }}
                      >
                        <Text
                          size={{ base: "schedule-item-tag-mobile", sm: "schedule-item-tag" }}
                          color="black !important"
                          borderColor="black !important"
                        >
                          { card.label }
                        </Text>
                      </Box>
                    }

                    {/* title */}
                    {card.title &&
                      <Box marginBottom={{ base: "16px !important", sm: "21px !important" }}>
                        <Heading
                          as="h3"
                          size={{ base: "portrait-slider-header-mobile", sm: "portrait-slider-header-tablet" }}
                          textAlign="left"
                          backgroundColor="rgba(0,0,0,0.01)"
                        >
                          { card.title }
                        </Heading>
                      </Box>
                    }

                    {/* button */}
                    {card.buttonText &&
                      <LinkBox>
                        <Button
                          size={{ base: "poster-slider-mobile", sm: "poster-slider" }}
                          backgroundColor="#4B8510 !important"
                        >
                          <LinkOverlay href={ card.slug }>
                            { card.buttonText }
                          </LinkOverlay>
                        </Button>
                      </LinkBox>
                    }

                  </Flex>
                </SwiperSlide>
              )
            })}
          </Swiper>

          {/* divider */}
          <Box
            backgroundColor="#000000"
            h={{ base: "5px", sm: "10px" }}
            w="100%"
            marginLeft="10px" 
          ></Box>
        </>
      }

      {/* desktop view */}
      {isLargerThan1200 &&
        <Grid
          templateColumns="repeat(2, 1fr)"
          gap="10px"
        >
          {articleItems.map(( card, index ) => {
            return (
              <GridItem
                key={ index }
                rowSpan={ 1 }
                colSpan={ index === 0 ? 2 : 1 }
                m={ index === 0 ? "0 10px" : "unset" }
              >

                {/* card 1 */}
                {index === 0 &&
                  <Box
                    w="100%"
                    h={{ base: "550px" }}
                    backgroundImage={ card.imageUrl }
                    aria-label={ card.imageAlt }
                    backgroundRepeat="no-repeat"
                    backgroundSize="cover"
                    backgroundPosition="center"
                  >
                    <LinkBox
                      display="flex" 
                      w="100%" 
                      h="100%"
                      p="70px 90px"
                      flexDirection="column"
                      justifyContent="flex-end !important"
                      alignItems="flex-start !important" 
                      // background="linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.008) 18.4%, rgba(0, 0, 0, 0.027) 33.8%, rgba(0, 0, 0, 0.06) 46.5%, rgba(0, 0, 0, 0.1) 56.8%, rgba(0, 0, 0, 0.15) 65%, rgba(0, 0, 0, 0.2) 71.5%, rgba(0, 0, 0, 0.255) 76.5%, rgba(0, 0, 0, 0.31) 80.3%, rgba(0, 0, 0, 0.365) 83.2%, rgba(0, 0, 0, 0.416) 85.6%, rgba(0, 0, 0, 0.463) 87.8%, rgba(0, 0, 0, 0.5) 90%, rgba(0, 0, 0, 0.533) 92.6%, rgba(0, 0, 0, 0.553) 95.8%, rgba(0, 0, 0, 0.56) 100%)"
                    >
                      <LinkOverlay href={ card.slug } aria-label={ card.buttonText }>
                        {/* label */}
                        { card.label &&
                          <Box
                            display="flex"
                            flexDirection="row"
                            gap="15px"
                            marginBottom={{ base: "18px", lg: "24px" }}
                          >
                            <Text 
                              size="schedule-item-tag"
                              textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                              backgroundColor="rgba(0,0,0,0.01)"
                            >
                              { card.label }
                            </Text>
                          </Box>
                        }

                        {/* heading */}
                        {card.title &&
                          <Box
                            marginBottom={{ base: "16px" }}
                            maxW="100%"
                          >
                            <Heading
                              as="h3"
                              size={{ base: "cards-header", "3xl": "cards-header-xl" }}
                              textAlign="left"
                              textShadow="0px 0px 16px rgba(0, 0, 0, 0.6)"
                              backgroundColor="rgba(0,0,0,0.01)"
                            >
                              { card.title }
                            </Heading>
                          </Box>
                        }

                        {/* button */}
                        {card.buttonText &&
                          <LinkBox>
                            <Button 
                              size={{ base: "poster-slider" }}
                              backgroundColor="#E31D40 !important"
                              _hover={{ 
                                bgColor: "#000000 !important", 
                                border: "none !important"
                              }}
                            >
                                { card.buttonText }
                              {/* <LinkOverlay href={ card.slug }>
                              </LinkOverlay> */}
                            </Button>
                          </LinkBox>
                        }
                      </LinkOverlay>
                    </LinkBox>
                  </Box>
                }

                {/* cards 2 - 5*/}
                {index > 0 &&
                  <Box 
                      key={ index }
                      position="relative"
                      paddingLeft={ (index === 1 || index === 3) ? "10px" : "unset"}
                      paddingRight={ (index === 2 || index === 4) ? "10px" : "unset"}
                    >
                    {card.video &&
                      <Box
                        h="473px"
                        w="100%"
                        as="video"
                        autoPlay
                        loop
                        muted
                        objectFit="cover"
                        poster={ card.imageUrl }
                      >
                        <source src={ card.video } type="video/mp4" />
                        <Image 
                          h="473px"
                          w="100%"
                          src={ card.imageUrl }
                          alt={ card.imageAlt }
                          boxSize="full"
                          objectFit="cover"
                          transition="0.3s ease-in-out" 
                        />
                      </Box>
                    }
                    {(!card.video && card.imageUrl) && 
                      <Image
                        h="473px"
                        w="100%"
                        src={ card.imageUrl }
                        alt={ card.imageAlt }
                        boxSize="full"
                        objectFit="cover"
                        transition="0.3s ease-in-out" 
                      />
                    }
                    <LinkBox
                      display="flex"
                      position="absolute"
                      top="0"
                      left="0"
                      h="100%"
                      w="100%"
                      bg="rgba(0, 0, 0, 0.3)"
                      opacity="0"
                      _hover={{
                        opacity: "1",
                        transform: "translateX(0)"
                      }}
                      _focusWithin={{
                        opacity: "1",
                        transform: "translateX(0)"
                      }}
                      transition="0.3s ease-in-out, transform 0.3s ease-out"
                      p={{ md: "50px 60px 40px 60px", "3xl": "50px 80px 75px 80px" }}
                      flexDirection="column"
                      justifyContent="flex-end !important"
                      backdropFilter="blur(2px)"
                      transform="translateX(-5px)"
                    >
                      <LinkOverlay href={ card.slug } aria-label={ card.buttonText }>
                        {/* label */}
                        {card.label &&
                          <Box
                            display="flex"
                            flexDirection="row"
                            gap="15px"
                            marginBottom={{ base: "18px", lg: "24px" }}
                          >
                            <Text size="schedule-item-tag" backgroundColor="rgba(0,0,0,0.01)">
                              { card.label }
                            </Text>
                          </Box>
                        }

                        {/* heading */}
                        {card.title &&
                          <Box
                            as="h3"
                            marginBottom={{ base: "16px" }}
                            maxW="100%"
                          >
                            <Heading
                              size={{ base: "portrait-slider-header-desktop" }}
                              textAlign="left"
                              marginBottom="16px !important"
                              backgroundColor="rgba(0,0,0,0.01)"
                            >
                              { card.title }
                            </Heading>
                          </Box>
                        }

                        {/* button */}
                        {card.buttonText &&
                          <Button
                            size="poster-slider"
                            backgroundColor="#4B8510 !important"
                            _hover={ isLargerThan1200 ? { bgColor: "#000000 !important", border: "none !important" } : { bgColor: "none", border: "none !important" }}
                          >
                            { card.buttonText }
                          </Button>
                        }
                      </LinkOverlay>
                    </LinkBox>
                  </Box>
                }

              </GridItem>
            )
          })}
        </Grid>
      }
    </Box>
  );
}

export default Cards;